import { create } from 'mobx-persist'
import localforage from 'localforage'
import remotedev from 'mobx-remotedev'
import { eventBus, subscribe } from 'mobx-event-bus2'
import { loading as baseLoading, loadingStore } from 'mobx-loading'
import { action as baseAction } from 'mobx'
import { lowerFirst } from 'lodash'

export * from 'mobx'
export * from 'mobx-react-lite'
export * from 'mobx-loading'
export * from 'mobx-event-bus2'
export * from 'mobx-persist'

export const storage = localforage

export const hydrate = create({ storage, jsonify: true })

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const parse = (raw) => {
	if (raw === null || raw === undefined) return null
	if (typeof raw !== 'string') return isPlainObject(raw) ? raw : null
	try {
		const parsed = JSON.parse(raw)
		return isPlainObject(parsed) ? parsed : null
	} catch (e) {
		return null
	}
}

/**
 * Fold `incoming` into `base`, two levels deep. `base` always wins: merging is
 * for adopting data a store used to keep somewhere else, never for overruling
 * what it already holds.
 *
 * Two levels is exactly what these blobs are — `{ appliedFilters: { [tableName]:
 * … } }` — and the second level is where the union has to happen, because the
 * whole point is that the two sources own DIFFERENT tables.
 */
const mergeBlobs = (base, incoming) => {
	if (!incoming) return base
	if (!base) return incoming
	const merged = { ...incoming, ...base }
	Object.keys(incoming).forEach((field) => {
		if (isPlainObject(base[field]) && isPlainObject(incoming[field])) {
			merged[field] = { ...incoming[field], ...base[field] }
		}
	})
	return merged
}

/**
 * Settle where this store's data lives, then hydrate from there.
 *
 * WHY THIS EXISTS
 * ---------------
 * The persistence key used to be `instance.constructor.name`, which production
 * mangles: terser renames every class, per chunk, and two stores in two chunks
 * are routinely given the SAME letter. On HAN Staff production
 * `TablePreferencesStore` and `generic-table-v2`'s `GenericTableStore` both
 * became `l`, so each one's autorun overwrote the other's blob — applying a
 * filter on a v2 tab wiped the user's hidden columns, and the next tab's column
 * seeding wiped the filter straight back out. Two stores can also share a name
 * before any minification at all (both GenericTableStores were literally
 * `GenericTableStore`).
 *
 * So the key is now an explicit string, and two things are carried into it:
 *
 *   legacyKey     — the name the running build gives the class. A store whose
 *                   stable key is still empty adopts that blob, so nobody loses
 *                   the preferences they already have.
 *   mergeFromKeys — keys belonging to a store that has since been folded into
 *                   this one (generic-table-v2). Their data is merged in and
 *                   the old key dropped, once.
 */
const hydratePersisted = (key, legacyKey, mergeFromKeys, instance) => {
	const merges = mergeFromKeys.filter((name) => name && name !== key)
	const adopts = legacyKey && legacyKey !== key
	if (!adopts && merges.length === 0) return hydrate(key, instance)

	const read = (name) => storage.getItem(name).catch(() => null)

	return Promise.all([read(key), adopts ? read(legacyKey) : Promise.resolve(null), ...merges.map(read)])
		.then(([ownRaw, legacyRaw, ...mergeRaws]) => {
			const own = parse(ownRaw)
			// Adopting is a fallback, never a merge: the name the build gave this
			// class may well belong to a DIFFERENT store (that is the whole bug),
			// so its blob is only safe to take when we would otherwise start empty.
			const base = own || parse(legacyRaw)
			// Merging is the opposite: these keys are known to be this store's own
			// data, kept elsewhere by a store that has since been folded in.
			const merged = mergeRaws.reduce((acc, raw) => mergeBlobs(acc, parse(raw)), base)
			if (!merged) return null
			const next = JSON.stringify(merged)
			return next === ownRaw ? null : storage.setItem(key, next)
		})
		.catch(() => null)
		.then(() => hydrate(key, instance))
		// A store that cannot hydrate is not a reason to leave the app broken, and
		// it is not a reason to keep the old key either: the data was written under
		// `key` above before hydrate was ever called.
		.catch(() => null)
		.then((result) => {
			merges.forEach((name) => storage.removeItem(name).catch(() => {}))
			return result
		})
}

/**
 * `persistKey` — the localforage key this store is persisted under. ALWAYS pass
 * one when `persist` is true: without it the key is the class name, which is
 * neither unique (two classes may share a name) nor stable (minification
 * renames it, differently in every build).
 */
export const store = (options) => (target) => {
	let decorator = function (...args) {
		const instance = new target(...args)
		const name = instance.constructor.name

		if (options?.persist) {
			hydratePersisted(options.persistKey || name, name, options.mergeFromKeys || [], instance)
		}

		remotedev(instance, { name })
		eventBus.register(instance)

		return instance
	}

	decorator.prototype = target.prototype
	return decorator
}

function fireEvent(func, eventName) {
	return function () {
		const payload = arguments[0]
		eventBus.post(eventName, payload)
		const promise = func.apply(this, arguments)

		if (!promise || typeof promise.then !== 'function') {
			return promise
		}

		return promise
			.then((data) => {
				eventBus.post(`${eventName}.success`, {
					event: `${eventName}.success`,
					data,
					payload,
				})
				return data
			})
			.catch((error) => {
				console.error(error)
				eventBus.post(`${eventName}.failure`, {
					event: `${eventName}.failure`,
					error,
					payload,
				})
			})
	}
}

export const baseEvent = (target, eventName, descriptor) => {
	const des = {
		enumerable: false,
		configurable: true,
		writable: true,
	}

	if (descriptor.value) {
		des.value = fireEvent(descriptor.value, eventName)
	} else {
		des.initializer = function () {
			return fireEvent(descriptor.initializer.call(this), eventName)
		}
	}

	return des
}

export const action = (target, key, descriptor) => {
	const eventName = `${lowerFirst(target.constructor.name)}/${key}`
	const func1 = baseEvent(target, eventName, descriptor)
	const func2 = baseLoading(eventName)(target, eventName, func1)

	return baseAction(target, eventName, func2)
}

export const event = (target, options) => {
	return function (...args) {
		let eventName = typeof target === 'string' ? target : target.name

		if (options?.success) {
			eventName = `${eventName}.success`
		} else if (options?.failure) {
			eventName = `${eventName}.failure`
		}

		subscribe(eventName)(...args)
		return baseAction(...args)
	}
}

export const loadingState = (target) => {
	const eventName = typeof target === 'string' ? target : target.name
	return loadingStore.actions[eventName]
}
